<template>
  <CustomModal ref="modal" title="Affecter à un dossier" class="stock-assign-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <p v-if="line" class="recap">
          <span class="recap-line">{{ line.designation }}</span>
          <span class="recap-purchase">{{ line.supplierName }}<template v-if="line.reference"> · {{ line.reference }}</template> · {{ formatQuantity(line.availableQuantity, line.unit) }} en stock</span>
        </p>
        <CustomSelectField :content=inputs.orderId />
        <div class="quantity">
          <CustomTextField :content=inputs.quantity />
          <span v-if="line && line.unit" class="suffix">{{ line.unit }}</span>
        </div>
        <p v-if="line" class="hint">Valeur imputée au dossier : {{ formatAmount(cost) }} HT ({{ formatAmount(line.unitCostHt) }} l'unité).</p>
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {NotificationService} from "@vues/v-common";
import {LogisticsController} from "@/purchase/controllers/LogisticsController";
import {formatAmount, formatQuantity, round2} from "@/purchase/utils";

// Modale d'affectation d'une quantité du stock à un dossier : openModal({line, orderValues}) avec une ligne de LogisticsController.getStock.
// L'affectation ajoute une part de quantité à la ventilation de l'achat (le coût reste porté par l'achat, il n'est pas compté deux fois).
// callback est appelé après l'affectation.
export default {
  name: 'StockAssignModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton},
  data() {
    return {
      line: undefined,
      inputs: {
        orderId: {label: 'Dossier', value: undefined, type: 'select', selectValues: [], required: true},
        quantity: {label: 'Quantité', value: '', type: 'number', step: 'any', required: true},
      },
      buttons: {
        submit: {text: "Affecter"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    quantity() {
      return Number(String(this.inputs.quantity.value ?? '').replace(',', '.')) || 0;
    },
    cost() {
      return this.line ? round2(this.quantity * this.line.unitCostHt) : 0;
    },
  },
  methods: {
    formatAmount,
    formatQuantity,
    openModal({line, orderValues}) {
      this.line = line;
      this.inputs.orderId.value = undefined;
      this.inputs.orderId.error = false;
      this.inputs.orderId.selectValues = orderValues;
      this.inputs.quantity.value = line.availableQuantity;
      this.inputs.quantity.error = false;
      this.$refs.modal.openModal();
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);
      if (!(this.quantity > 0) || this.quantity > this.line.availableQuantity + 0.0005) {
        this.inputs.quantity.error = true;
        (new NotificationService).addNotification("danger", `La quantité doit être comprise entre 0 et ${formatQuantity(this.line.availableQuantity, this.line.unit)}`);
        return;
      }

      const body = {purchaseId: this.line.purchaseId, lineId: this.line.lineId, orderId: this.inputs.orderId.value, quantity: this.quantity};
      const res = await this.$refs.form.submitForm(() => LogisticsController.assign(body));
      if (!res.errors) {
        (new NotificationService).addNotification("success", "Stock affecté au dossier");
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/purchase/style/section.styl';

.container
  display flex
  flex-direction column
  gap 10px

  form
    display flex
    flex-direction column
    gap 6px

    .recap
      display flex
      flex-direction column
      margin 0 0 4px

      .recap-line
        font-size 1.2em
        font-weight 600
        color THEME_COLOR_PRIMARY

      .recap-purchase
        color #6b7280

    .quantity
      position relative

      .suffix
        position absolute
        right 14px
        bottom 20px
        font-size 0.8em
        color #9ca3af
        pointer-events none

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Affecter]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 8px

      .button
        min-width 110px
</style>

<style lang="stylus">
.stock-assign-modal
  > .modal
    width 460px !important
    max-width 100%
    height auto !important
</style>
